import type { Location } from "@mmh/types"
import { computed } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useConcept } from "./concept"

/**
 * Temporary patch that matches costamesa path to match prod db costa-mesa path
 * Maps database path -> config/URL path
 * TODO: Remove once database is updated to use the correct paths
 */
const locationPathAliases: Record<string, string> = {
    "costa-mesa": "costamesa"
}

export function normalizeLocationPath(path: string): string {
    return locationPathAliases[path] || path
}

export function useLocation() {
    const route = useRoute()
    const router = useRouter()
    const { concept } = useConcept()

    const location = computed<Location | undefined>(() => {
        if (!concept.value?.locations) return undefined

        // Single-location concept: always return the only location
        if (concept.value.locations.length === 1) {
            return concept.value.locations[0]
        }

        // Multi-location concept: try to find by params.name first
        let locationName = route.params.name as string | undefined

        // Fallback: extract location from path if params.name unavailable
        // This handles SSG/SSR edge cases and direct navigation
        if (!locationName && route.path) {
            const pathMatch = route.path.match(/^\/([^/]+)/)
            if (pathMatch) {
                locationName = pathMatch[1]
            }
        }

        // Use normalizeLocationPath to handle path aliases (e.g., costa-mesa -> costamesa)
        const resolved = concept.value.locations.find((l: Location) =>
            normalizeLocationPath(l.path) === locationName
        )

        return resolved
    })

    /**
     * True only when a single-location site's location page is mounted at root.
     * Concept sites keep location-prefixed URLs even when only one location exists.
     * TODO: Remove this once we remove Jemma concept page.
     */
    const hasOneLocation = computed(() =>
        concept.value?.locations?.length === 1 && !router.hasRoute("concept")
    )

    function locationUrlPath() {
        if (hasOneLocation.value) return ""
        const path = location.value?.path
        return path ? `/${normalizeLocationPath(path)}` : `/${route.params.name}`
    }

    function menuPath(category?: string) {
        const prefix = locationUrlPath()
        return category ? `${prefix}/menus/${category}` : `${prefix}/menus`
    }

    function reservationsPath() {
        return `${locationUrlPath()}/reservations`
    }

    function contactPath() {
        return `${locationUrlPath()}/contact`
    }

    function absoluteUrl(path: string) {
        return `${concept.value.website}${path}`
    }

    return {
        location,
        hasOneLocation,
        locationUrlPath,
        menuPath,
        reservationsPath,
        contactPath,
        absoluteUrl
    }
}